import { useState, useEffect } from "react";
import { apiClient } from "@/App";
import { toast } from "sonner";
import {
  IndianRupee,
  Search,
  CheckCircle2,
  Clock,
  AlertCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export default function Payments() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    fetchPayments();
  }, [statusFilter]);

  const fetchPayments = async () => {
    try {
      let url = "/payments";
      if (statusFilter !== "all") url += `?status=${statusFilter}`;
      const response = await apiClient.get(url);
      setPayments(response.data);
    } catch (error) {
      console.error("Failed to fetch payments:", error);
      toast.error("Failed to load payments");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (paymentId, newStatus) => {
    try {
      await apiClient.put(`/payments/${paymentId}`, {
        status: newStatus,
        paid_date: newStatus === 'paid' ? new Date().toISOString().split('T')[0] : undefined
      });
      toast.success(`Payment marked as ${newStatus}`);
      fetchPayments();
    } catch (error) {
      toast.error("Failed to update payment");
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const filteredPayments = payments.filter(payment => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      payment.project_name?.toLowerCase().includes(query) ||
      payment.client_name?.toLowerCase().includes(query) ||
      payment.description?.toLowerCase().includes(query)
    );
  });

  const totalPending = payments.filter(p => p.status === 'pending').reduce((sum, p) => sum + p.amount, 0);
  const totalPaid = payments.filter(p => p.status === 'paid').reduce((sum, p) => sum + p.amount, 0);
  const totalOverdue = payments.filter(p => p.status === 'overdue').reduce((sum, p) => sum + p.amount, 0);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'paid':
        return <CheckCircle2 className="w-4 h-4 text-[#10B981]" />;
      case 'overdue':
        return <AlertCircle className="w-4 h-4 text-red-400" />;
      default:
        return <Clock className="w-4 h-4 text-[#F59E0B]" />;
    }
  };

  return (
    <div className="page-content" data-testid="payments-page">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Payments</h1>
          <p className="page-subtitle">Track all project payments and dues</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-[#1A1A1A] border border-[#262626] rounded p-4">
          <div className="flex items-center gap-2 mb-2">
            <Clock className="w-4 h-4 text-[#F59E0B]" />
            <p className="text-xs text-gray-500 uppercase tracking-wider">Pending</p>
          </div>
          <p className="text-2xl font-bold text-[#F59E0B]">{formatCurrency(totalPending)}</p>
        </div>
        <div className="bg-[#1A1A1A] border border-[#262626] rounded p-4">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle2 className="w-4 h-4 text-[#10B981]" />
            <p className="text-xs text-gray-500 uppercase tracking-wider">Received</p>
          </div>
          <p className="text-2xl font-bold text-[#10B981]">{formatCurrency(totalPaid)}</p>
        </div>
        <div className="bg-[#1A1A1A] border border-[#262626] rounded p-4">
          <div className="flex items-center gap-2 mb-2">
            <AlertCircle className="w-4 h-4 text-red-400" />
            <p className="text-xs text-gray-500 uppercase tracking-wider">Overdue</p>
          </div>
          <p className="text-2xl font-bold text-red-400">{formatCurrency(totalOverdue)}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 mb-6">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <Input
            placeholder="Search payments..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-[#171717] border-[#262626]"
          />
        </div>
        
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[150px] bg-[#171717] border-[#262626]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent className="bg-[#1A1A1A] border-[#262626]">
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
            <SelectItem value="overdue">Overdue</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Payments Table */}
      {loading ? (
        <div className="bg-[#1A1A1A] border border-[#262626] rounded">
          <div className="animate-pulse p-8">
            <div className="h-8 bg-[#262626] rounded mb-4"></div>
            <div className="space-y-3">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-12 bg-[#262626] rounded"></div>
              ))}
            </div>
          </div>
        </div>
      ) : filteredPayments.length > 0 ? (
        <div className="bg-[#1A1A1A] border border-[#262626] rounded overflow-hidden" data-testid="payments-table">
          <table className="data-table w-full">
            <thead>
              <tr>
                <th className="px-4">Project</th>
                <th className="px-4">Client</th>
                <th className="px-4">Description</th>
                <th className="px-4 text-right">Amount</th>
                <th className="px-4">Status</th>
                <th className="px-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredPayments.map((payment) => (
                <tr key={payment.payment_id} data-testid={`payment-${payment.payment_id}`}>
                  <td className="px-4">
                    <span className="font-medium text-white">{payment.project_name}</span>
                  </td>
                  <td className="px-4 text-gray-400">{payment.client_name}</td>
                  <td className="px-4">
                    <span className="text-gray-300">{payment.description}</span>
                    <span className="text-xs text-gray-500 block">Stage {payment.stage_number}</span>
                  </td>
                  <td className="px-4 text-right">
                    <span className="font-semibold text-white">{formatCurrency(payment.amount)}</span>
                  </td>
                  <td className="px-4">
                    <div className="flex items-center gap-2">
                      {getStatusIcon(payment.status)}
                      <span className={`badge ${
                        payment.status === 'paid' ? 'status-paid' :
                        payment.status === 'overdue' ? 'status-overdue' :
                        'status-pending'
                      }`}>
                        {payment.status}
                      </span>
                    </div>
                  </td>
                  <td className="px-4">
                    {payment.status !== 'paid' && (
                      <Button
                        size="sm"
                        onClick={() => handleStatusUpdate(payment.payment_id, 'paid')}
                        className="bg-[#10B981] hover:bg-[#059669]"
                      >
                        Mark Paid
                      </Button>
                    )}
                    {payment.status === 'paid' && payment.paid_date && (
                      <span className="text-xs text-gray-500">Paid: {payment.paid_date}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="empty-state">
          <div className="empty-state-icon">
            <IndianRupee />
          </div>
          <h3 className="empty-state-title">No payments found</h3>
          <p className="empty-state-description">
            {searchQuery || statusFilter !== "all"
              ? "Try adjusting your filters"
              : "Payments will appear when you create projects"}
          </p>
        </div>
      )}
    </div>
  );
}
